import React, { useState, useEffect } from 'react'
import { supabase } from '../lib/supabase'
import { C } from '../theme'

const SCHRITTE = ['Übernahme','Ersatzteile','Demontage','Lackvorbereitung','Lackierung','Lackkontrolle','Montage','Politur','Aufbereitung','Übergabe']

export default function Fahrzeugliste({ isMobile }) {
  const [fahrzeuge, setFahrzeuge]   = useState([])
  const [selected, setSelected]     = useState(null)
  const [historie, setHistorie]     = useState([])
  const [tab, setTab]               = useState('kunde')
  const [editMode, setEditMode]     = useState(false)
  const [editData, setEditData]     = useState({})
  const [saving, setSaving]         = useState(false)
  const [filter, setFilter]         = useState('aktiv')
  const [search, setSearch]         = useState('')
  const [loading, setLoading]       = useState(false)

  useEffect(() => { laden() }, [])

  const laden = async () => {
    setLoading(true)
    const { data } = await supabase.from('fahrzeuge').select('*').order('created_at', { ascending: false })
    setFahrzeuge(data || [])
    setLoading(false)
  }

  const openModal = async (f) => {
    setSelected(f)
    setTab('kunde')
    setEditMode(false)
    setEditData({
      kunde: f.kunde || '',
      telefon: f.telefon || '',
      marke: f.marke || '',
      modell: f.modell || '',
      farbe: f.farbe || '',
      wunschdatum: f.wunschdatum || '',
    })
    const { data } = await supabase
      .from('schritt_historie').select('*').eq('fahrzeug_id', f.id).order('created_at', { ascending: true })
    setHistorie(data || [])
  }

  const saveKunde = async () => {
    setSaving(true)
    await supabase.from('fahrzeuge').update(editData).eq('id', selected.id)
    setSelected({ ...selected, ...editData })
    setFahrzeuge(prev => prev.map(f => f.id === selected.id ? { ...f, ...editData } : f))
    setEditMode(false)
    setSaving(false)
  }

  const gefiltert = fahrzeuge.filter(f => {
    const q = search.toLowerCase()
    const matchSearch = !search ||
      f.kennzeichen?.toLowerCase().includes(q) ||
      f.kunde?.toLowerCase().includes(q) ||
      f.marke?.toLowerCase().includes(q) ||
      f.modell?.toLowerCase().includes(q)
    const matchFilter = filter === 'alle' || (filter === 'aktiv' && !f.übergabe_ok) || (filter === 'abgeschlossen' && f.übergabe_ok) || (filter === 'problem' && f.problem) || (filter === 'fastcars' && f.fastcars)
    return matchSearch && matchFilter
  })

  const getStatus = (f) => {
    if (f.übergabe_ok) return { text: 'Abgeschlossen', color: '#276749', bg: '#c6f6d5' }
    if (f.fastcars)    return { text: 'Bei FastCars',  color: '#553c9a', bg: '#e9d8fd' }
    if (f.problem)     return { text: `Problem (Schritt ${f.schritt})`, color: '#c53030', bg: '#fed7d7' }
    return { text: `Schritt ${f.schritt} – ${SCHRITTE[f.schritt - 1]}`, color: '#744210', bg: '#fefcbf' }
  }

  const getDeadline = (f) => {
    if (!f.wunschdatum) return null
    const diff = Math.floor((new Date(f.wunschdatum) - new Date()) / 86400000)
    const datum = new Date(f.wunschdatum).toLocaleDateString('de-DE')
    if (f.übergabe_ok) return { text: datum, color: '#276749' }
    if (diff < 0)  return { text: `${datum} (${Math.abs(diff)}T überfällig)`, color: '#c53030' }
    if (diff === 0) return { text: `${datum} (Heute!)`, color: '#dc2626' }
    if (diff <= 2)  return { text: `${datum} (${diff}T)`, color: '#d69e2e' }
    return { text: datum, color: '#276749' }
  }

  /* ── Print full list ── */
  const printListe = () => {
    const rows = gefiltert.map(f => {
      const status   = getStatus(f)
      const deadline = getDeadline(f)
      return `<tr>
        <td><strong>${f.kennzeichen}</strong></td>
        <td>${f.marke || ''} ${f.modell || ''}<br><small>${f.farbe || ''}</small></td>
        <td>${f.kunde || '–'}</td>
        <td>${f.telefon || '–'}</td>
        <td>${new Date(f.eingang).toLocaleDateString('de-DE')}</td>
        <td>${deadline ? deadline.text : '–'}</td>
        <td>${status.text}</td>
      </tr>`
    }).join('')
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Fahrzeugliste</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:20px}
      h1{font-size:16px;margin-bottom:4px}
      .meta{color:#888;font-size:10px;margin-bottom:14px}
      table{width:100%;border-collapse:collapse}
      th{background:#0d1b2a;color:#fff;padding:7px 10px;text-align:left;font-size:11px}
      td{padding:6px 10px;border-bottom:1px solid #e2e8f0;vertical-align:top}
      tr:nth-child(even) td{background:#f9f9f9}
      small{color:#888;font-size:10px}
      @media print{@page{margin:15mm}}
    </style></head>
    <body>
      <h1>Fahrzeugliste</h1>
      <div class="meta">${gefiltert.length} Fahrzeuge · Gedruckt: ${new Date().toLocaleString('de-DE')}${filter !== 'alle' ? ` · Filter: ${filter}` : ''}${search ? ` · Suche: "${search}"` : ''}</div>
      <table>
        <thead><tr><th>Kennzeichen</th><th>Fahrzeug</th><th>Kunde</th><th>Telefon</th><th>Eingang</th><th>Wunschtermin</th><th>Status</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </body></html>`
    const w = window.open('', '_blank')
    w.document.write(html); w.document.close(); w.print()
  }

  /* ── Print Verlauf ── */
  const printVerlauf = () => {
    const entries = historie.map(h => `
      <div class="entry">
        <div class="entry-time">${new Date(h.created_at).toLocaleString('de-DE')}${h.mitarbeiter ? ` <span class="ma">[${h.mitarbeiter}]</span>` : ''}</div>
        <div class="entry-text">${h.text}</div>
      </div>`).join('')
    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Verlauf ${selected.kennzeichen}</title>
    <style>
      body{font-family:Arial,sans-serif;font-size:12px;margin:20px}
      h1{font-size:16px;margin-bottom:4px}
      .meta{color:#888;font-size:10px;margin-bottom:16px}
      .entry{padding:8px 0;border-bottom:1px solid #e2e8f0}
      .entry-time{font-size:10px;color:#888;margin-bottom:3px}
      .entry-text{font-size:12px}
      .ma{color:#4299e1}
      @media print{@page{margin:15mm}}
    </style></head>
    <body>
      <h1>Verlauf: ${selected.kennzeichen}</h1>
      <div class="meta">${selected.marke || ''} ${selected.modell || ''} · ${selected.farbe || ''} · Eingang: ${new Date(selected.eingang).toLocaleDateString('de-DE')}</div>
      <div class="meta">Kunde: ${selected.kunde || '–'} · Telefon: ${selected.telefon || '–'} · Gedruckt: ${new Date().toLocaleString('de-DE')}</div>
      ${entries || '<p style="color:#888">Kein Verlauf vorhanden.</p>'}
    </body></html>`
    const w = window.open('', '_blank')
    w.document.write(html); w.document.close(); w.print()
  }

  return (
    <div style={{ padding: 24 }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap', gap: 10 }}>
        <h2 style={{ margin: 0 }}>Alle Fahrzeuge</h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {[['alle','Alle'],['aktiv','Aktiv'],['abgeschlossen','Abgeschlossen'],['problem','Problem'],['fastcars','FastCars']].map(([v, l]) => (
            <button key={v} onClick={() => setFilter(v)} style={{
              padding: '6px 12px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
              background: filter === v ? C.primary : '#e2e8f0', color: filter === v ? C.onPrimary : '#4a5568',
            }}>{l}</button>
          ))}
          <input style={{ padding: '6px 12px', border: '1px solid #ddd', borderRadius: 6, fontSize: 13, width: 180 }}
            placeholder="Suchen..." value={search} onChange={e => setSearch(e.target.value)} />
          <button onClick={printListe} style={{ padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151', whiteSpace: 'nowrap' }}>
            🖨 Liste drucken
          </button>
        </div>
      </div>

      {/* Mobile: Kartenliste */}
      {!loading && isMobile && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {gefiltert.map(f => {
            const status   = getStatus(f)
            const deadline = getDeadline(f)
            return (
              <div key={f.id} onClick={() => openModal(f)} style={{
                background: '#fff', borderRadius: 10, padding: '12px 14px', cursor: 'pointer',
                boxShadow: '0 1px 4px rgba(0,0,0,0.07)', borderLeft: `4px solid ${f.problem ? C.danger : f.fastcars ? C.fastcars : C.primary}`,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                  <div style={{ fontWeight: 800, fontSize: 15 }}>{f.kennzeichen}</div>
                  <span style={{ background: status.bg, color: status.color, borderRadius: 5, padding: '3px 8px', fontSize: 11, fontWeight: 700, whiteSpace: 'nowrap' }}>{status.text}</span>
                </div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 3 }}>{f.marke} {f.modell}{f.farbe ? ` · ${f.farbe}` : ''}</div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginTop: 5 }}>
                  <span style={{ color: '#374151' }}>{f.kunde || '–'}</span>
                  {deadline && <span style={{ color: deadline.color, fontWeight: 600 }}>{deadline.text}</span>}
                </div>
              </div>
            )
          })}
          {gefiltert.length === 0 && <div style={{ textAlign: 'center', padding: 32, color: '#999' }}>Keine Fahrzeuge</div>}
        </div>
      )}

      {/* Desktop: Tabelle */}
      {loading ? <p>Wird geladen...</p> : !isMobile && (
        <div style={{ overflowX: 'auto', borderRadius: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.07)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Kennzeichen','Fahrzeug','Kunde','Telefon','Eingang','Termin','Status',''].map(h => (
                  <th key={h} style={{ background: C.dark, color: '#fff', padding: '10px 12px', textAlign: 'left', fontSize: 12, borderBottom: `3px solid ${C.primary}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {gefiltert.map((f, i) => {
                const status   = getStatus(f)
                const deadline = getDeadline(f)
                return (
                  <tr key={f.id} style={{ background: i % 2 === 0 ? '#fff' : '#f9f9f9', cursor: 'pointer' }} onClick={() => openModal(f)}>
                    <td style={td}><strong>{f.kennzeichen}</strong></td>
                    <td style={td}>{f.marke} {f.modell}<br /><span style={{ color: '#999', fontSize: 11 }}>{f.farbe}</span></td>
                    <td style={td}>{f.kunde || <span style={{ color: '#ccc' }}>–</span>}</td>
                    <td style={td}>{f.telefon || <span style={{ color: '#ccc' }}>–</span>}</td>
                    <td style={td}>{new Date(f.eingang).toLocaleDateString('de-DE')}</td>
                    <td style={td}>{deadline ? <span style={{ color: deadline.color, fontWeight: 600 }}>{deadline.text}</span> : <span style={{ color: '#bbb' }}>–</span>}</td>
                    <td style={td}><span style={{ background: status.bg, color: status.color, borderRadius: 5, padding: '3px 8px', fontSize: 12, fontWeight: 600 }}>{status.text}</span></td>
                    <td style={td}><button style={{ background: '#e2e8f0', border: 'none', borderRadius: 5, padding: '4px 10px', cursor: 'pointer', fontSize: 12 }}>Öffnen →</button></td>
                  </tr>
                )
              })}
              {gefiltert.length === 0 && <tr><td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#999' }}>Keine Fahrzeuge</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modal ── */}
      {selected && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
          <div style={isMobile
            ? { background: '#fff', width: '100%', height: '100%', overflowY: 'auto', display: 'flex', flexDirection: 'column' }
            : { background: '#fff', borderRadius: 14, width: '92%', maxWidth: 700, maxHeight: '90vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>

            {/* Modal Header */}
            <div style={{ padding: '20px 24px 0', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 18 }}>{selected.kennzeichen}</h3>
                  <div style={{ color: '#888', fontSize: 12, marginTop: 3 }}>{selected.marke} {selected.modell} · {selected.farbe} · Eingang: {new Date(selected.eingang).toLocaleDateString('de-DE')}</div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#999', lineHeight: 1 }}>✕</button>
              </div>

              {/* Tabs */}
              <div style={{ display: 'flex', gap: 0 }}>
                {[['kunde','👤 Kundendaten'],['verlauf','📋 Verlauf']].map(([k, l]) => (
                  <button key={k} onClick={() => setTab(k)} style={{
                    padding: '8px 18px', border: 'none', borderBottom: tab === k ? '3px solid #1d4ed8' : '3px solid transparent',
                    background: 'none', cursor: 'pointer', fontWeight: tab === k ? 700 : 500,
                    color: tab === k ? '#1d4ed8' : '#6b7280', fontSize: 13,
                  }}>{l}</button>
                ))}
              </div>
            </div>

            {/* Tab: Kundendaten */}
            {tab === 'kunde' && (
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0d1b2a' }}>Kundeninformationen</div>
                  {!editMode
                    ? <button onClick={() => setEditMode(true)} style={btnEdit}>✏️ Bearbeiten</button>
                    : <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => setEditMode(false)} style={btnCancel}>Abbrechen</button>
                        <button onClick={saveKunde} disabled={saving} style={btnSave}>{saving ? 'Speichern...' : '✓ Speichern'}</button>
                      </div>
                  }
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  {[
                    { label: 'Kundenname', key: 'kunde' },
                    { label: 'Telefon', key: 'telefon' },
                    { label: 'Marke', key: 'marke' },
                    { label: 'Modell', key: 'modell' },
                    { label: 'Farbe', key: 'farbe' },
                  ].map(({ label, key }) => (
                    <div key={key}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>{label}</div>
                      {editMode
                        ? <input style={inputStyle} value={editData[key]} onChange={e => setEditData({ ...editData, [key]: e.target.value })} />
                        : <div style={{ fontSize: 14, color: editData[key] ? '#111827' : '#d1d5db', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>{editData[key] || '–'}</div>
                      }
                    </div>
                  ))}
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>Wunschtermin</div>
                    {editMode
                      ? <input type="date" style={inputStyle} value={editData.wunschdatum} onChange={e => setEditData({ ...editData, wunschdatum: e.target.value })} />
                      : <div style={{ fontSize: 14, color: editData.wunschdatum ? '#111827' : '#d1d5db', padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
                          {editData.wunschdatum ? new Date(editData.wunschdatum).toLocaleDateString('de-DE') : '–'}
                        </div>
                    }
                  </div>
                </div>

                {/* Status Info */}
                <div style={{ marginTop: 20, background: '#f8fafc', borderRadius: 8, padding: '12px 16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 13 }}>
                  <div><span style={{ color: '#6b7280' }}>Status: </span><strong>{selected.übergabe_ok ? '✅ Abgeschlossen' : `Schritt ${selected.schritt} – ${SCHRITTE[selected.schritt - 1]}`}</strong></div>
                  <div><span style={{ color: '#6b7280' }}>Übergabe: </span><strong>{selected.übergabe_datum ? new Date(selected.übergabe_datum).toLocaleDateString('de-DE') : '–'}</strong></div>
                  {selected.fastcars && <div><span style={{ color: '#6b7280' }}>FastCars: </span><strong>{selected.fastcars_notiz || 'Ja'}</strong></div>}
                  {selected.leihwagen_id && <div><span style={{ color: '#6b7280' }}>Leihwagen: </span><strong>Vergeben</strong></div>}
                </div>
              </div>
            )}

            {/* Tab: Verlauf */}
            {tab === 'verlauf' && (
              <div style={{ padding: '20px 24px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#0d1b2a' }}>Aktivitätsverlauf</div>
                  <button onClick={printVerlauf} style={{ padding: '6px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 600, color: '#374151' }}>
                    🖨 Verlauf drucken
                  </button>
                </div>
                {historie.length === 0 && <p style={{ color: '#999' }}>Kein Verlauf vorhanden.</p>}
                <div style={{ position: 'relative', paddingLeft: 22 }}>
                  {historie.map((h, i) => (
                    <div key={h.id} style={{ display: 'flex', gap: 12, marginBottom: 10, position: 'relative' }}>
                      <div style={{ position: 'absolute', left: -22, top: 5, width: 11, height: 11, borderRadius: '50%', background: h.typ === 'problem' ? '#dc2626' : h.typ === 'fastcars' ? '#7c3aed' : h.typ === 'übergabe' ? '#48bb78' : '#4299e1' }} />
                      {i < historie.length - 1 && <div style={{ position: 'absolute', left: -17, top: 16, width: 2, height: 'calc(100% + 4px)', background: '#e2e8f0' }} />}
                      <div style={{ flex: 1, background: '#f9f9f9', borderRadius: 6, padding: '8px 12px', fontSize: 13 }}>
                        <div style={{ color: '#999', fontSize: 11, marginBottom: 2 }}>
                          {new Date(h.created_at).toLocaleString('de-DE')}
                          {h.mitarbeiter && <span style={{ color: '#4299e1', marginLeft: 6 }}>[{h.mitarbeiter}]</span>}
                        </div>
                        <div>{h.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  )
}

const td         = { padding: '9px 12px', verticalAlign: 'middle', borderBottom: '1px solid #f0f0f0' }
const inputStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box', outline: 'none' }
const btnEdit    = { padding: '6px 14px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 }
const btnSave    = { padding: '6px 16px', background: '#1d4ed8', color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontSize: 12, fontWeight: 700 }
const btnCancel  = { padding: '6px 14px', background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb', borderRadius: 7, cursor: 'pointer', fontSize: 12 }
